import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Cadre } from './cadre';
import {
  AdresseVerifiee,
  Autoriser,
  BoiteMail,
  CHEMIN_AUTORISER,
  Connexion,
  Inscription,
  MotDePasseOublie,
  NouveauMotDePasse,
  PagePublique,
  SupprimerMonCompte,
  cheminDUnLien,
  pagePubliqueDe,
} from './comptes';
import { Dossiers, Feuille } from './projets';
import { Maintenant, Journee } from './maintenant';
import { Agenda } from './agenda';
import { Accueil, VersLaPageDAccueil } from './accueil';
import { Parametres } from './apparence';
import { Profil } from './profil';
import { BoiteDEntree } from './boite';
import { VersLaFiche } from './fiche';
import { cles } from './partage/relectures';
import { useRejouerLaFile, useSession } from './partage/useArbre';
import { useAppareilDesGestes } from './partage/useContexteDesGestes';

/** Les écrans de qui n'est pas connecté (chantier mails : la boîte mail et le mot de passe oublié). */
type EcranSansCompte = 'connexion' | 'inscription' | 'oubli' | { boite: string };

function ecranDuChemin(chemin: string): EcranSansCompte {
  if (chemin === '/inscription') return 'inscription';
  if (chemin === '/mot-de-passe-oublie') return 'oubli';
  return 'connexion';
}

/**
 * Les chemins où la feuille s'ouvre d'emblée, sans passer par la vitrine. La page Autoriser du
 * connecteur de claude.ai (chantier connecteur-claude, ticket 02) en est : sans session, la
 * connexion s'ouvre sur place, et la page revient une fois la session ouverte.
 */
const CHEMINS_SANS_VITRINE = ['/connexion', '/inscription', '/mot-de-passe-oublie', CHEMIN_AUTORISER];

export function App() {
  const client = useQueryClient();
  const session = useSession();
  // Qui n'est pas connecté voit la connexion, ou l'inscription s'il l'a demandée (ticket 03).
  // `/inscription` l'ouvre d'emblée : c'est le bouton « Créer mon compte » de la vitrine.
  const [ecran, setEcran] = useState<EcranSansCompte>(() => ecranDuChemin(window.location.pathname));
  useRejouerLaFile(session.data?.utilisateur ?? null);
  // L'appareil, joint à chaque requête pour le journal des gestes (chantier journal-des-gestes, 05).
  useAppareilDesGestes();

  // Confidentialité et mentions légales : lisibles par tous, connectés ou non (ticket 05), donc
  // avant de regarder la session. On y arrive par un lien simple, on en repart de même.
  const pagePublique = pagePubliqueDe(window.location.pathname);
  if (pagePublique) return <PagePublique chemin={pagePublique} />;

  // Les pages où mènent les liens des mails (chantier mails), connecté ou non : le lien ouvre
  // la session de son compte, puis l'app repart de zéro.
  const lien = cheminDUnLien(window.location.pathname);
  if (lien === '/verifier-adresse') return <AdresseVerifiee />;
  if (lien === '/nouveau-mot-de-passe') return <NouveauMotDePasse />;
  // Le lien du mail « Supprimer ton compte » (chantier parametres-du-compte) : une dernière question.
  if (lien === '/supprimer-mon-compte') return <SupprimerMonCompte />;

  if (session.isPending) {
    // Rien de clignotant : l'app s'ouvre sur du calme, pas sur un chargeur qui tourne.
    return <div style={{ minHeight: '100%' }} />;
  }

  // Claude demande l'accès (chantier connecteur-claude, ticket 02) : la page Autoriser, seule, hors du cadre.
  if (session.data?.connecte && window.location.pathname === CHEMIN_AUTORISER) return <Autoriser />;

  if (!session.data?.connecte) {
    /*
     * Qui arrive sans être connecté voit d'abord la vitrine (`client/public/vitrine/`), sauf sur
     * `/connexion` et `/inscription`, où mènent ses boutons (demande d'Igor du 2026-10-21).
     */
    const chemin = window.location.pathname;
    if (!CHEMINS_SANS_VITRINE.includes(chemin) && ecran === 'connexion') {
      window.location.replace('/vitrine/');
      return null;
    }
    // La connexion se referme une fois la session relue, pas avant. Après « sortir », on revient
    // à la connexion.
    const entrer = () => {
      void client.invalidateQueries({ queryKey: cles.session }).then(() => setEcran('connexion'));
    };
    const versLaConnexion = () => setEcran('connexion');
    const versLaBoite = (email: string) => setEcran({ boite: email });
    if (typeof ecran === 'object') return <BoiteMail email={ecran.boite} onRetour={() => setEcran('inscription')} />;
    if (ecran === 'oubli') return <MotDePasseOublie onRetour={versLaConnexion} />;
    if (ecran === 'inscription') return <Inscription onAVerifier={versLaBoite} onRetour={versLaConnexion} />;
    return (
      <Connexion
        onEntre={entrer}
        onCreerUnCompte={() => setEcran('inscription')}
        onOubli={() => setEcran('oubli')}
        onAVerifier={versLaBoite}
      />
    );
  }

  return (
    <BrowserRouter>
      <Cadre>
        <Routes>
          {/* L'accueil, la carte de ta vie, à l'ordinateur (chantier navigation, 02) ; au téléphone, il ramène à Maintenant. */}
          <Route path="/accueil" element={<Accueil />} />
          <Route path="/dossiers" element={<Dossiers />} />
          <Route path="/feuille/:id" element={<Feuille />} />
          {/*
            L'adresse de la fiche d'une bulle (chantier notifications-socle, ticket 03) : le toucher
            d'une notification y mène ; elle ouvre la fiche chez la mère de la bulle, comme un
            toucher dans un projet, et ne reste pas dans l'historique.
          */}
          <Route path="/bulle/:id" element={<VersLaFiche />} />
          <Route path="/maintenant" element={<Maintenant />} />
          <Route path="/agenda" element={<Agenda />} />
          {/* L'onglet Journée du téléphone (chantier telephone-cadre, ticket 01). */}
          <Route path="/journee" element={<Journee />} />
          {/*
            Les Paramètres de l'ordinateur, un item de la barre de gauche (chantier navigation,
            01). Au téléphone, les siens s'ouvrent dans le cadre, et cette adresse
            ramène à Maintenant.
          */}
          <Route path="/parametres" element={<Parametres />} />
          {/* Le Profil (chantier profil, ticket 02) ; au téléphone, sa page à lui (chantier telephone-profil, 01). */}
          <Route path="/profil" element={<Profil />} />
          {/* La boîte d'entrée, à l'ordinateur seulement (chantier boite-d-entree, ticket 02) ; au téléphone, l'adresse ramène à Maintenant. */}
          <Route path="/boite" element={<BoiteDEntree />} />
          {/*
            L'écran « Aujourd'hui » est retiré (chantier telephone-cadre, ticket 01, décision
            d'Igor du 2026-10-05) : son adresse, gardée dans un favori ou l'historique, mène à
            Maintenant.
          */}
          <Route path="/aujourdhui" element={<Navigate to="/maintenant" replace />} />
          {/*
            L'ordinateur s'ouvre sur l'accueil, la carte de ta vie (chantier navigation, 02, décision
            d'Igor du 2026-10-19) ; le téléphone sur « Maintenant » (chantier telephone-cadre,
            ticket 01), qui accueille sans projet avec « Par quoi tu commences ? » (ticket 14 de
            retours-testeurs), comme l'accueil.
          */}
          <Route path="*" element={<VersLaPageDAccueil />} />
        </Routes>
      </Cadre>
    </BrowserRouter>
  );
}
